import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ArrowLeft } from "lucide-react";
import { adminGetNewsOne, adminCreateNews, adminUpdateNews, adminGetBrands, mediaUrl } from "@/lib/api";
import { AdminShell } from "@/components/admin/AdminShell";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { MediaPickerDialog } from "@/components/admin/MediaPickerDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const inputStyle = { background: "var(--surface)", borderColor: "var(--card-border)", color: "var(--text)" };
const CATEGORIES = ["Announcement", "Venture Story", "Group Update", "Milestone"];
const EMPTY = { title: "", slug: "", excerpt: "", content: "", category: "Venture Story", status: "draft", coverMediaId: null, publishedAt: "", seoTitle: "", seoDescription: "", ogImage: null, relatedBrandSlug: "" };

const Field = ({ label, hint, children }) => (
  <div>
    <label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>{label}</label>
    {children}
    {hint && <p className="mt-1 text-[11px]" style={{ color: "var(--fg-subtle)" }}>{hint}</p>}
  </div>
);
const Input = (props) => <input {...props} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />;
const Area = (props) => <textarea {...props} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />;

function toDateInput(iso) {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
}

export default function AdminNewsEditor() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [coverPicker, setCoverPicker] = useState(false);
  const [ogPicker, setOgPicker] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);

  const { data: existing, isLoading } = useQuery({ queryKey: ["admin-news", id], queryFn: () => adminGetNewsOne(id), enabled: isEdit });
  const { data: brands = [] } = useQuery({ queryKey: ["admin-brands"], queryFn: adminGetBrands });

  useEffect(() => {
    if (existing) { setForm({ ...EMPTY, ...existing, publishedAt: toDateInput(existing.publishedAt) }); setSlugEdited(true); }
  }, [existing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTitle = (v) => {
    setForm((f) => {
      const next = { ...f, title: v };
      if (!slugEdited) next.slug = v.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      return next;
    });
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-news"] });
    qc.invalidateQueries({ queryKey: ["news"] });
    qc.invalidateQueries({ queryKey: ["homepage-news"] });
  };

  const persist = async (status) => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        excerpt: form.excerpt,
        content: form.content,
        category: form.category,
        status,
        coverMediaId: form.coverMediaId || null,
        publishedAt: form.publishedAt ? new Date(form.publishedAt).toISOString() : undefined,
        seoTitle: form.seoTitle,
        seoDescription: form.seoDescription,
        ogImage: form.ogImage || null,
        relatedBrandSlug: form.relatedBrandSlug || null,
      };
      if (isEdit) {
        await adminUpdateNews(id, payload);
        invalidate();
        toast.success(status === "published" ? "Published" : "Saved");
      } else {
        const created = await adminCreateNews(payload);
        invalidate();
        toast.success(status === "published" ? "Published" : "Draft saved");
        navigate(`/admin/news/edit/${created.id}`, { replace: true });
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const coverUrl = form.coverMediaId ? mediaUrl(form.coverMediaId) : null;
  const ogUrl = form.ogImage ? mediaUrl(form.ogImage) : null;
  const published = form.status === "published";

  if (isEdit && isLoading) {
    return <AdminShell title="Edit Article"><div className="h-96 animate-pulse rounded-[14px] border" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }} /></AdminShell>;
  }

  return (
    <AdminShell
      title={isEdit ? "Edit Article" : "New Article"}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => persist("draft")} disabled={saving} data-testid="admin-news-save-draft" className="inline-flex items-center gap-2 rounded-[10px] border px-4 py-2 text-sm font-medium disabled:opacity-60" style={{ borderColor: "var(--card-border)", color: "var(--text)" }}>
            {saving && <Loader2 size={15} className="animate-spin" />} Save Draft
          </button>
          <button onClick={() => persist("published")} disabled={saving} data-testid="admin-news-publish" className="inline-flex items-center gap-2 rounded-[10px] px-4 py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
            {saving && <Loader2 size={15} className="animate-spin" />} {published ? "Update" : "Publish"}
          </button>
        </div>
      }
    >
      <button onClick={() => navigate("/admin/news")} className="mb-4 inline-flex items-center gap-2 text-sm" style={{ color: "var(--muted)" }}><ArrowLeft size={15} /> Back to News</button>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Main column */}
        <div className="space-y-4">
          <Field label="Title *">
            <Input data-testid="admin-news-title" value={form.title} onChange={(e) => onTitle(e.target.value)} placeholder="Article headline" />
          </Field>
          <Field label="URL Slug" hint={`Public URL: /news/${form.slug || "..."}`}>
            <Input data-testid="admin-news-slug" value={form.slug} onChange={(e) => { setSlugEdited(true); set("slug", e.target.value); }} placeholder="article-url-slug" />
          </Field>
          <Field label="Excerpt / Summary" hint="A short 1\u20133 line summary shown on the News listing and used as the default meta description.">
            <Area data-testid="admin-news-excerpt" rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
          </Field>
          <Field label="Article Content">
            <RichTextEditor value={form.content} onChange={(html) => set("content", html)} />
          </Field>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="rounded-[12px] border p-4" style={{ borderColor: "var(--card-border)", background: "var(--surface)" }}>
            <p className="mb-3 text-[11px] uppercase" style={{ color: "var(--fg-subtle)", letterSpacing: "0.18em" }}>Publishing</p>
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span style={{ color: "var(--muted)" }}>Status</span>
                <span className="rounded-full px-2.5 py-0.5 text-[11px]" style={published ? { background: "rgba(140,106,61,0.16)", color: "var(--accent-fg)" } : { background: "rgba(255,255,255,0.05)", color: "var(--muted)" }} data-testid="admin-news-status-badge">{published ? "Published" : "Draft"}</span>
              </div>
              <Field label="Publication Date"><Input data-testid="admin-news-date" type="date" value={form.publishedAt} onChange={(e) => set("publishedAt", e.target.value)} /></Field>
              <Field label="Category">
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="admin-news-category" style={inputStyle}><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Related Company (optional)">
                <Select value={form.relatedBrandSlug || "none"} onValueChange={(v) => set("relatedBrandSlug", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="admin-news-related" style={inputStyle}><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {brands.map((b) => <SelectItem key={b.id} value={b.slug}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>

          <div className="rounded-[12px] border p-4" style={{ borderColor: "var(--card-border)", background: "var(--surface)" }}>
            <p className="mb-3 text-[11px] uppercase" style={{ color: "var(--fg-subtle)", letterSpacing: "0.18em" }}>Featured Image</p>
            <div className="overflow-hidden rounded-[10px] border" style={{ borderColor: "var(--card-border)", background: "var(--bg-elev-1)", aspectRatio: "16 / 10" }}>
              {coverUrl ? <img src={coverUrl} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-xs" style={{ color: "var(--muted)" }}>No image</div>}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button type="button" onClick={() => setCoverPicker(true)} data-testid="admin-news-cover-picker" className="rounded-[10px] border px-3 py-2 text-sm" style={{ borderColor: "var(--card-border)", color: "var(--text)" }}>Choose Image</button>
              {form.coverMediaId && <button type="button" onClick={() => set("coverMediaId", null)} className="text-xs" style={{ color: "var(--danger)" }}>Remove</button>}
            </div>
          </div>

          <div className="rounded-[12px] border p-4" style={{ borderColor: "var(--card-border)", background: "var(--surface)" }}>
            <p className="mb-3 text-[11px] uppercase" style={{ color: "var(--fg-subtle)", letterSpacing: "0.18em" }}>SEO</p>
            <div className="space-y-4">
              <Field label="SEO Title" hint="Defaults to the article title if left blank."><Input data-testid="admin-news-seo-title" value={form.seoTitle} onChange={(e) => set("seoTitle", e.target.value)} /></Field>
              <Field label="Meta Description" hint="Defaults to the excerpt if left blank."><Area data-testid="admin-news-seo-desc" rows={3} value={form.seoDescription} onChange={(e) => set("seoDescription", e.target.value)} /></Field>
              <Field label="Open Graph Image" hint="Defaults to the featured image if left blank.">
                <div className="flex items-center gap-2">
                  <div className="h-12 w-20 overflow-hidden rounded-md border" style={{ borderColor: "var(--card-border)", background: "var(--bg-elev-1)" }}>
                    {ogUrl ? <img src={ogUrl} alt="" className="h-full w-full object-cover" /> : null}
                  </div>
                  <button type="button" onClick={() => setOgPicker(true)} className="rounded-[10px] border px-3 py-2 text-sm" style={{ borderColor: "var(--card-border)", color: "var(--text)" }}>Choose</button>
                  {form.ogImage && <button type="button" onClick={() => set("ogImage", null)} className="text-xs" style={{ color: "var(--danger)" }}>Remove</button>}
                </div>
              </Field>
            </div>
          </div>
        </div>
      </div>

      <MediaPickerDialog open={coverPicker} onOpenChange={setCoverPicker} onSelect={(id) => set("coverMediaId", id)} />
      <MediaPickerDialog open={ogPicker} onOpenChange={setOgPicker} onSelect={(id) => set("ogImage", id)} />
    </AdminShell>
  );
}
